import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import { Platform } from 'react-native';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../firebase/firebase';
import { getCurrentUser } from './auth-service';
import loggingService from './logging-service';
import { logEvent } from './analytics-service';

// Configure notification handler
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

export interface PushNotificationData {
  title: string;
  body: string;
  data?: Record<string, any>;
  categoryId?: string;
}

export interface NotificationPermissionStatus {
  granted: boolean;
  canAskAgain: boolean;
  ios?: {
    status: Notifications.IosAuthorizationStatus;
    allowsAlert: boolean;
    allowsSound: boolean;
    allowsBadge: boolean;
  };
}

class PushNotificationService {
  private notificationListener: any = null;
  private responseListener: any = null;

  /**
   * Initialize push notifications
   */
  async initialize(): Promise<void> {
    try {
      // Check if we're on a physical device
      if (!Device.isDevice) {
        loggingService.warn('Push notifications require a physical device');
        return;
      }

      // Register notification categories for interactive notifications
      await this.registerNotificationCategories();

      // Set up notification listeners
      this.setupNotificationListeners();

      loggingService.info('Push notification service initialized');
    } catch (error) {
      loggingService.error('Failed to initialize push notifications', error);
    }
  }

  /**
   * Request push notification permissions
   */
  async requestPermissions(): Promise<NotificationPermissionStatus> {
    try {
      const { status: existingStatus } = await Notifications.getPermissionsAsync();
      
      let finalStatus = existingStatus;
      
      if (existingStatus !== 'granted') {
        const { status } = await Notifications.requestPermissionsAsync();
        finalStatus = status;
      }

      if (finalStatus !== 'granted') {
        loggingService.warn('Push notification permission denied');
        return { granted: false, canAskAgain: true };
      }

      // Get detailed iOS permissions
      if (Platform.OS === 'ios') {
        const settings = await Notifications.getPermissionsAsync();
        return {
          granted: true,
          canAskAgain: false,
          ios: settings.ios,
        };
      }

      return { granted: true, canAskAgain: false };
    } catch (error) {
      loggingService.error('Error requesting push notification permissions', error);
      return { granted: false, canAskAgain: false };
    }
  }

  /**
   * Get push notification token
   */
  async getExpoPushToken(): Promise<string | null> {
    try {
      if (!Device.isDevice) {
        return null;
      }

      const token = await Notifications.getExpoPushTokenAsync({
        projectId: process.env.EXPO_PUBLIC_EAS_PROJECT_ID,
      });

      return token.data;
    } catch (error) {
      loggingService.error('Failed to get push token', error);
      return null;
    }
  }

  /**
   * Register push token for current user
   */
  async registerPushToken(): Promise<boolean> {
    try {
      const user = getCurrentUser();
      if (!user) {
        loggingService.warn('No authenticated user to register push token');
        return false;
      }

      const permissions = await this.requestPermissions();
      if (!permissions.granted) {
        loggingService.warn('Push notifications not granted');
        return false;
      }

      const token = await this.getExpoPushToken();
      if (!token) {
        loggingService.warn('Failed to get push token');
        return false;
      }

      // Update user document with push token
      const userRef = doc(firestore, 'users', user.uid);
      await updateDoc(userRef, {
        pushToken: token,
        pushTokenUpdatedAt: serverTimestamp(),
        notificationSettings: {
          enabled: true,
          ...(permissions.ios && {
            ios: {
              alert: permissions.ios.allowsAlert,
              sound: permissions.ios.allowsSound,
              badge: permissions.ios.allowsBadge,
            },
          }),
        },
      });

      logEvent('push_token_registered', {
        platform: Platform.OS,
        tokenLength: token.length,
      });

      loggingService.info('Push token registered successfully');
      return true;
    } catch (error) {
      loggingService.error('Failed to register push token', error);
      return false;
    }
  }

  /**
   * Unregister push token for current user
   */
  async unregisterPushToken(): Promise<void> {
    try {
      const user = getCurrentUser();
      if (!user) return;

      const userRef = doc(firestore, 'users', user.uid);
      await updateDoc(userRef, {
        pushToken: null,
        pushTokenUpdatedAt: serverTimestamp(),
        'notificationSettings.enabled': false,
      });

      loggingService.info('Push token unregistered');
    } catch (error) {
      loggingService.error('Failed to unregister push token', error);
    }
  }

  /**
   * Schedule a local notification
   */
  async scheduleLocalNotification(
    notification: PushNotificationData,
    trigger: Notifications.NotificationTriggerInput
  ): Promise<string> {
    try {
      const id = await Notifications.scheduleNotificationAsync({
        content: {
          title: notification.title,
          body: notification.body,
          data: notification.data || {},
          categoryIdentifier: notification.categoryId,
        },
        trigger,
      });

      logEvent('local_notification_scheduled', {
        title: notification.title,
        triggerType: trigger ? 'scheduled' : 'immediate',
      });

      return id;
    } catch (error) {
      loggingService.error('Failed to schedule notification', error);
      throw error;
    }
  }

  /**
   * Cancel a scheduled notification
   */
  async cancelScheduledNotification(notificationId: string): Promise<void> {
    try {
      await Notifications.cancelScheduledNotificationAsync(notificationId);
      loggingService.info('Notification cancelled', { notificationId });
    } catch (error) {
      loggingService.error('Failed to cancel notification', error);
    }
  }

  /**
   * Get all scheduled notifications
   */
  async getScheduledNotifications(): Promise<Notifications.NotificationRequest[]> {
    try {
      return await Notifications.getAllScheduledNotificationsAsync();
    } catch (error) {
      loggingService.error('Failed to get scheduled notifications', error);
      return [];
    }
  }

  /**
   * Set badge count (iOS only)
   */
  async setBadgeCount(count: number): Promise<void> {
    if (Platform.OS === 'ios') {
      try {
        await Notifications.setBadgeCountAsync(count);
      } catch (error) {
        loggingService.error('Failed to set badge count', error);
      }
    }
  }

  /**
   * Clear all notifications
   */
  async clearAllNotifications(): Promise<void> {
    try {
      await Notifications.dismissAllNotificationsAsync();
      await this.setBadgeCount(0);
    } catch (error) {
      loggingService.error('Failed to clear notifications', error);
    }
  }

  /**
   * Set up notification listeners
   */
  private setupNotificationListeners(): void {
    // Handle notifications received while app is foregrounded
    this.notificationListener = Notifications.addNotificationReceivedListener(notification => {
      loggingService.info('Notification received', {
        title: notification.request.content.title,
        body: notification.request.content.body,
      });

      logEvent('push_notification_received', {
        title: notification.request.content.title,
        foreground: true,
      });
    });

    // Handle user interaction with notifications
    this.responseListener = Notifications.addNotificationResponseReceivedListener(response => {
      const { notification, actionIdentifier } = response;
      
      loggingService.info('Notification response', {
        title: notification.request.content.title,
        action: actionIdentifier,
      });

      logEvent('push_notification_opened', {
        title: notification.request.content.title,
        action: actionIdentifier,
      });

      // Handle navigation based on notification data
      this.handleNotificationResponse(notification.request.content.data, actionIdentifier);
    });
  }

  /**
   * Handle notification response
   */
  private handleNotificationResponse(data: any, actionIdentifier: string): void {
    // Handle different notification types
    switch (data.type) {
      case 'invoice':
        // Navigate to invoice detail
        if (data.invoiceId) {
          // Navigation will be handled by the app navigator
          loggingService.info('Navigate to invoice', { invoiceId: data.invoiceId });
        }
        break;
      
      case 'payment':
        // Navigate to payment screen
        if (data.paymentId) {
          loggingService.info('Navigate to payment', { paymentId: data.paymentId });
        }
        break;
      
      case 'invitation':
        // Navigate to invitation screen
        if (data.invitationToken) {
          loggingService.info('Navigate to invitation', { token: data.invitationToken });
        }
        break;
      
      default:
        // Navigate to dashboard
        loggingService.info('Navigate to dashboard');
    }
  }

  /**
   * Register notification categories for interactive notifications
   */
  private async registerNotificationCategories(): Promise<void> {
    if (Platform.OS === 'ios') {
      await Notifications.setNotificationCategoryAsync('invoice_action', [
        {
          identifier: 'view',
          buttonTitle: 'View Invoice',
          options: {
            opensAppToForeground: true,
          },
        },
        {
          identifier: 'approve',
          buttonTitle: 'Approve',
          options: {
            opensAppToForeground: false,
          },
        },
      ]);

      await Notifications.setNotificationCategoryAsync('payment_action', [
        {
          identifier: 'view',
          buttonTitle: 'View Details',
          options: {
            opensAppToForeground: true,
          },
        },
        {
          identifier: 'pay',
          buttonTitle: 'Pay Now',
          options: {
            opensAppToForeground: true,
          },
        },
      ]);
    }
  }

  /**
   * Clean up notification listeners
   */
  cleanup(): void {
    if (this.notificationListener) {
      Notifications.removeNotificationSubscription(this.notificationListener);
    }
    if (this.responseListener) {
      Notifications.removeNotificationSubscription(this.responseListener);
    }
  }
}

export default new PushNotificationService();